import { useState } from "react";
import CollapsibleSection from "../../../common/CollapsibleSection";
import useSettings from "../../../../hooks/Settings";
import { BaseWidget, WIDGET_REGISTRY } from "../widgets";
import { isAvailableForPersona, isModuleEnabled } from "./widgetBrowserGating";
import isTemplateMode from "../widgets/_shared/isTemplateMode";
import useWidgetPersona from "../widgets/_shared/useWidgetPersona";
import usePersonaTerms from "../../../../hooks/usePersonaTerms";

/**
 * Vaste koppen. 'medewerkers' (de deelnemerwidgets) staat er niet in: die kop
 * volgt hoe de omgeving deelnemers noemt (inbox #325).
 * @type {Object.<string, string>}
 */
const categories = {
    default: 'Algemeen',
    research: 'Onderzoek',
    huishoudens: 'Huishoudens',
}

const PERSONA_CATEGORY = 'medewerkers';

/**
 * Widget browser for selecting widgets in the live report builder.
 *
 * @param {object} props
 * @param {(WidgetClass: typeof BaseWidget) => void} props.onClickWidget
 * @returns
 */
export const WidgetBrowser = ({
    onClickWidget
}) => {
    const { housevisits, personasOn } = useSettings();
    const moduleFlags = { housevisits, personasOn };
    const isTemplate = isTemplateMode();
    const personaId = useWidgetPersona();
    const personaTerms = usePersonaTerms();

    const allWidgets = Object.entries(WIDGET_REGISTRY)
        .filter(([_key, WidgetClass]) => isModuleEnabled(WidgetClass, moduleFlags, isTemplate))
        .filter(([_key, WidgetClass]) => isAvailableForPersona(WidgetClass, personaId));

    // map categories to their widgets
    /** @type {Record<string, [string, typeof BaseWidget][]>} */
    const categorizedWidgets = allWidgets.reduce((acc, [key, WidgetClass]) => {
        const category = WidgetClass.prototype.getCategory();
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push([key, WidgetClass]);
        return acc;
    }, /** @type {Record<string, [string, typeof BaseWidget][]>} */ ({}));

    /* Gaat het rapport over één deelnemer, dan horen zijn widgets bovenaan
       in plaats van als laatste groep onder de vouw (inbox #325). */
    const orderedCategories = Object.entries(categorizedWidgets).sort(([a], [b]) => (
        personaId ? Number(b === PERSONA_CATEGORY) - Number(a === PERSONA_CATEGORY) : 0
    ));

    return (
        <div className="widget-browser">
            {orderedCategories.map(
                ([category, widgets]) => (
                    <WidgetCategorySection
                        key={category}
                        category={category}
                        title={category === PERSONA_CATEGORY ? personaTerms.plural : (categories[category] || category)}
                        widgets={widgets}
                        onClickWidget={onClickWidget}
                    />
                )
            )}
        </div>
    );
}

/**
 * @param {object} props
 * @param {string} props.category - Category key, used as section key
 * @param {string} props.title - Heading shown above the category
 * @param {[string, typeof BaseWidget][]} props.widgets
 * @param {(WidgetClass: typeof BaseWidget) => void} props.onClickWidget
 * @returns 
 */
const WidgetCategorySection = ({ category, title, widgets, onClickWidget }) => {
    const [isOpen, setIsOpen] = useState(true); 

    return (
        <CollapsibleSection key={category} 
            title={title}
            sectionKey={category}
            withContainer={false}
            isOpen={isOpen}
            onToggle={() => setIsOpen(prev => !prev)}
            >
            {/* <h3>{categories[category] || category}</h3> */}
            <div className="widget-browser-items">
                {widgets.map(([type, WidgetClass]) => (
                    <div
                        className="widget-browser-item"
                        draggable
                        onDragStart={(e) => {
                            e.stopPropagation(); // Prevent parent widgets from also handling drag
                            e.dataTransfer.setData('type', type);
                            // setDragging(true);
                        }}
                        // onDragEnd={(e) => {
                        //     setDragging(false);
                        // }}
                        key={type}
                        onClick={() => onClickWidget(WidgetClass)}
                    >
                        <div className="widget-browser-item-icon">
                            {WidgetClass.prototype.getIcon()}
                        </div>
                        <span className="widget-browser-item-label">
                            {WidgetClass.prototype.getName()}
                        </span>
                    </div>
                ))}
            </div>
        </CollapsibleSection>
    );
}
